import type { ComputedRef, InjectionKey } from 'vue'

/**
 * Profile management context provided by the host widget
 * (apps/web-component) and injected by `PkChatbot` / `PkChatbotViewProfile`.
 *
 * Keeping it injectable (rather than prop-drilled) lets the shared chat
 * components stay decoupled from the better-auth client: the widget owns the
 * auth client and exposes only the high-level actions the profile panel needs.
 * When the context is absent (e.g. anonymous session, or a host that doesn't
 * wire it up) the profile panel falls back to a read-only stub.
 */

export interface ChatbotProfileUser {
    id: string
    name?: string | null
    email?: string | null
    image?: string | null
    isAnonymous?: boolean | null
}

export interface ChatbotProfileContext {
    /** The authenticated, non-anonymous user, or null. */
    user: ComputedRef<ChatbotProfileUser | null>
    /** Whether the profile panel should be reachable / rendered. */
    canManageProfile: ComputedRef<boolean>
    /**
     * Whether the agent has at least one auth method enabled, so the chat can
     * offer a voluntary sign-in to anonymous users (`auth` view, filled by
     * the host through the `view-auth` slot).
     */
    canSignIn?: ComputedRef<boolean>
    /**
     * URL of the profile management page on the main site, or undefined when
     * the installation did not say where its app is: no link then.
     */
    profileUrl: ComputedRef<string | undefined>
    /**
     * External token mode: the host application owns the auth lifecycle, so
     * the widget offers no sign-out nor profile management affordances.
     * Defaults to false when absent.
     */
    isExternalAuth?: ComputedRef<boolean>
    signOut: () => Promise<void>
    /**
     * GDPR B.3 — erases ALL conversations of the current session's user,
     * anonymous sessions included. When the backend deletes the (PII-free)
     * anonymous account along with them, `accountDeleted` is true and the
     * host has already cleared its session cookies.
     */
    eraseConversations?: () => Promise<{ accountDeleted: boolean }>
}

export const ChatbotProfileContextKey: InjectionKey<ChatbotProfileContext> =
    Symbol('chatbotProfileContext')
